import React, { useCallback, useEffect, useState } from 'react';
import { Gift, TrendingDown, CheckCircle2, Loader2, Clock, Layers, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import client from '../../lib/api';
import { accountHook } from '../../lib/domHooks';

export const money = (v) => `${Number(v || 0).toFixed(2)} ₺`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-');
const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-');

// Anlık ve kayan pencere tekrarlanabilirdir: aynı pencerede yeni kayıp
// oluştukça tekrar talep edilir. Günlük/haftalık dönem başına tek talep alır.
export const REPEATABLE_PERIODS = ['instant', 'rolling'];
export const isRepeatable = (period) => REPEATABLE_PERIODS.includes(period);

export const periodLabel = (period, hours) => {
  if (period === 'rolling') return `Son ${hours || 24} saat`;
  if (period === 'instant') return 'Anlık';
  return period === 'weekly' ? 'Haftalık' : 'Günlük';
};

const VERTICAL_TITLE = {
  all: 'Kayıp Bonusu',
  sports: 'Spor Kayıp Bonusu',
  casino: 'Casino Kayıp Bonusu',
};

export const verticalTitle = (data, block) => (
  data?.split_enabled
    ? (VERTICAL_TITLE[block.vertical] || VERTICAL_TITLE.all)
    : VERTICAL_TITLE.all
);

// Ayrım kapalıyken tek bir birleşik kart döner; sunucu her iki hâlde de
// aynı listeyi verdiği için sayfanın iki ayrı çizim yolu yoktur.
export const lossBonusBlocks = (data) => {
  if (!data?.enabled) return [];
  return data.verticals?.length
    ? data.verticals
    : [{
      vertical: 'all', label: 'Spor + Casino', tiers: data.tiers,
      wager_multiplier: data.wager_multiplier, min_payout: data.min_payout,
      max_bonus: data.max_bonus, claimable: data.claimable, current: data.current,
    }];
};

/**
 * Kayıp bonusunun verisi ve talebi. Hesap sayfası (`/hesap/kayip-bonusu`) ile
 * Bonuslar sayfasındaki kart (pages/Promotions.jsx) aynı kaynağı okur.
 *
 * ``active`` false iken hiç istek atılmaz (ziyaretçi ya da kartı çizmeyen
 * şema). ``quiet``: yükleme hatası toast'a yazılmaz — Bonuslar sayfasında
 * kayıp bonusu yan bilgidir, kampanya listesinin üstüne hata basmamalıdır.
 */
export const useLossBonus = ({ active = true, quiet = false, withHistory = true } = {}) => {
  const { setWalletBalances } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(active);
  const [busy, setBusy] = useState('');
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    if (!active) {
      setData(null);
      setLoading(false);
      return;
    }
    try {
      const [{ data: s }, h] = await Promise.all([
        client.get('/cashback/summary'),
        withHistory ? client.get('/cashback/history') : Promise.resolve(null),
      ]);
      setData(s);
      if (h) setHistory(h.data.items || []);
    } catch (e) {
      if (!quiet) {
        toast({ title: 'Hata', description: e.response?.data?.detail || 'Bilgiler yüklenemedi' });
      }
    } finally {
      setLoading(false);
    }
  }, [active, quiet, withHistory, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!active || !isRepeatable(data?.period)) return undefined;
    let alive = true;
    const refresh = async () => {
      try {
        const { data: summary } = await client.get('/cashback/summary');
        if (alive) setData(summary);
      } catch (_) {
        // Arka plan yenilemesi sessiz kalır; manuel işlem hataları toast ile gösterilir.
      }
    };
    const timer = window.setInterval(refresh, 15000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [active, data?.period]);

  const claim = async (vertical) => {
    setBusy(vertical);
    try {
      const { data: res } = await client.post(
        `/cashback/claim?vertical=${encodeURIComponent(vertical)}`);
      setWalletBalances(res);
      toast({
        title: 'Bonus Alındı',
        description: `${money(res.amount)} hesabınıza tanımlandı (%${res.percent} kayıp bonusu)`,
      });
      await load();
    } catch (e) {
      toast({ title: 'Bonus alınamadı', description: e.response?.data?.detail || 'İşlem başarısız' });
    } finally {
      setBusy('');
    }
  };

  return {
    loading, data, history, busy, claim, blocks: lossBonusBlocks(data),
  };
};

// Geçmiş satırının dönemi: tekrarlanabilir modlarda takvim anahtarı
// anlamsızdır (pencere takvime bağlı değildir), talebin anı yazılır.
const historyPeriod = (item) => (
  isRepeatable(item.period)
    ? `${fmtDate(item.period_end)} ${fmtTime(item.period_end)}`
    : item.period_key
);

const historyLabel = (item) => (
  item.vertical && item.vertical !== 'all'
    ? (item.vertical === 'sports' ? 'Spor' : 'Casino')
    : '—'
);

// ``compact``: kart Bonuslar sayfasındaki detay penceresinde çizilir — dış
// panel çerçevesi pencerenin kendisidir ve özet dört değil iki sütundur.
export function VerticalCard({
  data, block, busy, onClaim, compact = false,
}) {
  const c = block.claimable;
  const cur = block.current;
  const repeatable = isRepeatable(data.period);
  const label = periodLabel(data.period, data.rolling_hours);
  const range = repeatable
    ? `${label} · ${fmtTime(c.period_start)} – ${fmtTime(c.period_end)}`
    : `${fmtDate(c.period_start)}${data.period === 'weekly' ? ` - ${fmtDate(c.period_end)}` : ''}`;
  const title = verticalTitle(data, block);

  return (
    <div className={compact ? '' : 'pusu-panel p-6'} {...accountHook('panel')} data-bhs-vertical={block.vertical}>
      <div className={`flex items-center gap-3 mb-1 ${compact ? 'pr-10' : ''}`} {...accountHook('page-header')}>
        <Gift size={20} className="text-[var(--pusu-gold)]" />
        <h2
          id={compact ? 'loss-bonus-detail-title' : undefined}
          className="text-xl font-bold gold-text"
          {...accountHook('title')}
        >
          {title}
        </h2>
        <span className="ml-auto badge-pill badge-yellow" {...accountHook('badge')}>{label}</span>
      </div>
      <p className="text-sm text-gray-400 mb-5" {...accountHook('subtitle')}>
        {repeatable
          ? 'Net kaybınız canlı hesaplanır; hak doğduğu anda bonusunuzu alabilirsiniz.'
          : `${label} net kaybınız üzerinden kademeli iade. Talep, tamamlanmış dönem için yapılır.`}
      </p>

      {/* Talep edilebilir dönem */}
      <div className={`grid ${compact ? 'grid-cols-2 gap-3' : 'grid-cols-1 md:grid-cols-4 gap-4'} mb-5`} {...accountHook('summary')}>
        <div className="pusu-card p-4" {...accountHook('stat')}>
          <div className="text-xs text-gray-400">Net Kayıp</div>
          <div className="text-2xl font-black text-red-400 mt-1">{money(c.net_loss)}</div>
          <div className="flex items-center gap-1 mt-2 text-[11px] text-gray-500">
            <TrendingDown size={11} /> {range}
          </div>
        </div>
        <div className="pusu-card p-4" {...accountHook('stat')}>
          <div className="text-xs text-gray-400">Bonus Oranı</div>
          <div className="text-2xl font-black gold-text mt-1">%{Number(c.percent || 0).toFixed(0)}</div>
          <div className="text-[11px] text-gray-500 mt-2">
            {block.wager_multiplier > 0 ? `${block.wager_multiplier}x çevrim` : 'Çevrimsiz'}
          </div>
        </div>
        <div className="pusu-card p-4" {...accountHook('stat')}>
          <div className="text-xs text-gray-400">Bonus Tutarı</div>
          <div className="text-2xl font-black gold-text mt-1">{money(c.amount)}</div>
          <div className="text-[11px] text-gray-500 mt-2">
            {c.funding_notice
              ? 'Net para girişi yetersiz'
              : c.capped
                ? `Üst limit: ${money(block.max_bonus)}`
                : c.can_claim
                  ? 'Hemen alınabilir'
                  : repeatable && c.claimed_amount > 0
                    ? 'Yeni kayıp bekleniyor'
                    : c.already_claimed ? 'Talep edildi' : 'Hak doğmadı'}
          </div>
        </div>
        <div className="pusu-card p-4" {...accountHook('stat')}>
          <div className="text-xs text-gray-400">Bahis / Kazanç</div>
          <div className="text-lg font-bold text-white mt-1.5">{money(c.total_bet)}</div>
          <div className="text-[11px] text-green-400 mt-1">Kazanç: {money(c.total_win)}</div>
        </div>
      </div>

      {/* Red gerekçesi sunucudan **cümle olarak** gelir ve talep reddinde
          gösterilen metnin aynısıdır: ikisi ayrışsaydı oyuncu sayfada başka,
          düğmeye basınca başka bir sebep okurdu. */}
      {c.funding_notice && (
        <div className="pusu-card p-4 mb-5 flex items-start gap-3" {...accountHook('notice')}>
          <AlertCircle size={16} className="text-yellow-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold">Bonus hakkı doğmadı</div>
            <div className="text-xs text-gray-400 mt-1">{c.funding_notice}</div>
          </div>
        </div>
      )}

      {/* Anlık durum / devam eden dönem */}
      <div className="pusu-card p-4 mb-5 flex items-start gap-3" {...accountHook('notice')}>
        <Clock size={16} className="text-[var(--pusu-gold)] mt-0.5 shrink-0" />
        <div className="flex-1">
          {repeatable ? (
            <>
              <div className="text-sm font-semibold">Canlı hesaplama aktif ({label})</div>
              <div className="text-xs text-gray-400 mt-1">
                Bu pencerede alınan: <span className="gold-text font-semibold">{money(c.claimed_amount)}</span>
                {' · '}Bonuslanan kayıp: <span className="text-red-400 font-semibold">{money(c.claimed_loss)}</span>
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Veriler 15 saniyede bir yenilenir. Yeni net kayıp oluştuğunda tekrar talep edebilirsiniz.
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold">Devam eden dönem ({cur.period_key})</div>
              <div className="text-xs text-gray-400 mt-1">
                Şu anki net kayıp: <span className="text-red-400 font-semibold">{money(cur.net_loss)}</span>
                {' · '}Tahmini bonus: <span className="gold-text font-semibold">{money(cur.amount)}</span> (%{Number(cur.percent || 0).toFixed(0)})
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Bu dönem tamamlandığında talep edilebilir hale gelir.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Kademeler */}
      {block.tiers?.length > 0 && (
        <div className="pusu-card p-4 mb-5" {...accountHook('tiers')}>
          <div className="flex items-center gap-2 text-sm font-semibold mb-3">
            <Layers size={15} className="text-[var(--pusu-gold)]" /> Bonus Kademeleri
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[...block.tiers].sort((a, b) => a.min_loss - b.min_loss).map((t, i) => {
              const active = Number(c.tier_min_loss) === Number(t.min_loss) && c.percent > 0;
              return (
                <div
                  key={i}
                  className={`${active ? 'account-tier-active' : 'account-tier'} flex items-center justify-between px-3 py-2.5 rounded-lg`}
                  data-bhs-status={active ? 'active' : undefined}
                  {...accountHook('tier')}
                >
                  <span className="text-xs text-gray-400">{money(t.min_loss)}+ kayıp</span>
                  <span className={`text-sm font-bold ${active ? 'gold-text' : 'text-gray-300'}`}>%{t.percent}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Şartlar */}
      <div className="pusu-card p-5" {...accountHook('terms')}>
        <div className="flex items-start gap-3">
          <CheckCircle2 size={20} className="text-green-400 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Bonus Şartları</div>
            <ul className="text-sm text-gray-400 mt-2 space-y-1 list-disc list-inside">
              <li>
                Net kayıp = toplam bahis − toplam kazanç
                {data.split_enabled && block.vertical !== 'all'
                  ? ` (yalnızca ${block.vertical === 'sports' ? 'spor kuponları' : 'casino turları'})`
                  : ' (spor + casino)'}
              </li>
              <li>Kayıp, aynı pencerede kasaya giren net para (yatırım − çekim) kadar bonuslanır</li>
              {c.min_net_funding > 0 && (
                <li>Bu pencerede en az {money(c.min_net_funding)} net para girişi gerekir</li>
              )}
              <li>
                {block.wager_multiplier > 0
                  ? `Bonus ${block.wager_multiplier}x çevrim şartına tabidir`
                  : 'Bonus çevrimsizdir, anında hesabınıza tanımlanır'}
              </li>
              <li>Minimum ödeme tutarı {money(block.min_payout)}</li>
              {block.max_bonus > 0 && (
                <li>{repeatable ? 'Pencere başına' : 'Dönem başına'} en fazla {money(block.max_bonus)} bonus verilir</li>
              )}
              <li>
                {repeatable
                  ? 'Aynı kayıp ikinci kez bonuslanmaz; yeni net kayıp oluştuğunda tekrar talep edilebilir'
                  : 'Her dönem için yalnızca bir kez talep edilebilir'}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Talep butonu */}
      <div className="flex items-center justify-between gap-4 mt-5 flex-wrap" {...accountHook('actions')}>
        <div className="text-xs text-gray-500 flex items-center gap-1.5">
          {c.funding_notice ? (
            <><AlertCircle size={13} className="text-yellow-500" /> Çekim/yatırım dengesi bonusa uygun değil</>
          ) : repeatable ? (
            c.can_claim
              ? <>Kullanılabilir yeni kayıp: {money(c.available_loss)}</>
              : <><AlertCircle size={13} className="text-yellow-500" /> Yeni bonus hakkı bekleniyor</>
          ) : c.already_claimed ? (
            <><CheckCircle2 size={13} className="text-green-400" /> {c.period_key} dönemi için bonus alındı</>
          ) : !c.eligible ? (
            <><AlertCircle size={13} className="text-yellow-500" /> Bu dönemde bonus hakkı doğmadı</>
          ) : (
            <>Dönem: {c.period_key}</>
          )}
        </div>
        <button onClick={() => onClaim(block.vertical)} {...accountHook('submit')}
                disabled={busy || !c.can_claim}
                className="gold-btn px-6 py-3 rounded-xl flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
          {!repeatable && c.already_claimed
            ? 'Zaten Alındı'
            : `Bonusu Al${c.amount > 0 ? ` (${money(c.amount)})` : ''}`}
        </button>
      </div>
    </div>
  );
}

const LossBonus = () => {
  const {
    loading, data, history, busy, claim, blocks,
  } = useLossBonus();

  if (loading) {
    return (
      <div className="pusu-panel p-10 text-center text-gray-400" {...accountHook('loading')}>
        <Loader2 className="inline animate-spin mr-2 text-[var(--pusu-gold)]" /> Yükleniyor...
      </div>
    );
  }

  if (!data?.enabled) {
    return (
      <div className="pusu-panel p-6" {...accountHook('panel')}>
        <div className="flex items-center gap-3 mb-4" {...accountHook('page-header')}>
          <Gift size={20} className="text-[var(--pusu-gold)]" />
          <h2 className="text-xl font-bold gold-text" {...accountHook('title')}>Kayıp Bonusu</h2>
        </div>
        <div className="pusu-card p-6 text-center text-gray-400" {...accountHook('empty')}>
          Kayıp bonusu şu anda kapalı. Daha sonra tekrar deneyin.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {blocks.map((block) => (
        <VerticalCard
          key={block.vertical}
          data={data}
          block={block}
          busy={busy === block.vertical}
          onClaim={claim}
        />
      ))}

      {/* Geçmiş */}
      {history.length > 0 && (
        <div className="pusu-panel p-6" {...accountHook('list')}>
          <div className="text-sm font-semibold mb-4" {...accountHook('title')}>Bonus Geçmişi</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]" {...accountHook('table')}>
              <thead>
                <tr className="text-xs text-gray-400 border-b border-white/5">
                  <th className="text-left py-2">DÖNEM</th>
                  {data.split_enabled && <th className="text-left py-2">TÜR</th>}
                  <th className="text-right py-2">BAHİS</th>
                  <th className="text-right py-2">KAZANÇ</th><th className="text-right py-2">NET KAYIP</th>
                  <th className="text-right py-2">ORAN</th><th className="text-right py-2">BONUS</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-white/5" {...accountHook('row')}>
                    <td className="py-2.5 font-semibold">{historyPeriod(h)}</td>
                    {data.split_enabled && (
                      <td className="py-2.5 text-gray-300">{historyLabel(h)}</td>
                    )}
                    <td className="py-2.5 text-right text-gray-400">{money(h.total_bet)}</td>
                    <td className="py-2.5 text-right text-green-400">{money(h.total_win)}</td>
                    <td className="py-2.5 text-right text-red-400">{money(h.net_loss)}</td>
                    <td className="py-2.5 text-right text-gray-300">%{Number(h.percent).toFixed(0)}</td>
                    <td className="py-2.5 text-right gold-text font-bold">{money(h.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LossBonus;
